import type { AttachmentRow } from '@/shared/lib/attachment-rows';
import { ExternalLink, FileText } from 'lucide-react';
import { Text } from '../text';
import css from './attachments-panel.module.scss';

type AttachmentsPanelProps = {
  attachments: AttachmentRow[];
  emptyMessage: string;
};

/**
 * Documents available on a product or item, as rows rather than thumbnails —
 * filename, format and Internal/Public matter more here than a visual preview.
 *
 * Files open in a new tab so the preview stays open and keeps its state. The
 * anchors are plain `href`s, so the browser decides between rendering and
 * downloading; nothing forces a download on a format it could display.
 */
export const AttachmentsPanel = ({
  attachments,
  emptyMessage,
}: AttachmentsPanelProps) => {
  if (attachments.length === 0) {
    return (
      <div className={css.root}>
        <Text as="p" size="sm" variant="muted">
          {emptyMessage}
        </Text>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <div className={css.scroll}>
        <table className={css.table}>
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">Type</th>
              <th scope="col">Visibility</th>
              <th className={css.actionCol} scope="col">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {attachments.map((attachment) => (
              <tr key={attachment.key}>
                <td>
                  <span className={css.file}>
                    <FileText className={css.fileIcon} size={18} />
                    {attachment.url ? (
                      <a
                        className={css.fileLink}
                        href={attachment.url}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {attachment.fileName}
                      </a>
                    ) : (
                      <span>{attachment.fileName}</span>
                    )}
                  </span>
                </td>
                <td>{attachment.fileType || '—'}</td>
                <td>{attachment.isPublic ? 'Public' : 'Internal'}</td>
                <td className={css.actionCol}>
                  {attachment.url ? (
                    <a
                      className={css.open}
                      href={attachment.url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Open
                      <ExternalLink size={13} />
                    </a>
                  ) : (
                    // No stored location — listed for completeness, but there
                    // is nothing to open.
                    <Text as="span" size="xs" variant="muted">
                      Unavailable
                    </Text>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Text as="p" size="xs" variant="muted">
        Files open in a new tab.
      </Text>
    </div>
  );
};
